import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import {
  calculateAllocationSummary,
  calculateSuggestedAllocateAmount,
  calculateTotalOutstandingAfterReceipt,
  matchReceiptAmount,
  amountsEqual,
  formatSignedAmount,
  parseSignedAmount,
  roundMoney,
} from "../../../../../utils/receiptAllocation";
import "./ReceiptForm.css";
import { showMessageBox } from '../../../../site/fn';

const isPendingApprovalStatus = (status) =>
  status === "Pending Approval" || status === "Pending";

const getStatusBadge = (approvalStatus, isDraft) => {
  if (isDraft || approvalStatus === "Draft") {
    return <span className="status-badge draft">Draft</span>;
  }
  if (approvalStatus === "Approved") {
    return <span className="status-badge approved">Approved</span>;
  }
  if (approvalStatus === "Rejected") {
    return <span className="status-badge rejected">Rejected</span>;
  }
  if (isPendingApprovalStatus(approvalStatus)) {
    return <span className="status-badge pending">Pending Approval</span>;
  }
  return null;
};

const ReceiptForm = ({ rowId }) => {
  const navigate = useNavigate();
  const isEditMode = rowId !== null && rowId !== undefined;

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Form data
  const [formData, setFormData] = useState({
    receipt_date: new Date().toISOString().split("T")[0],
    customer_id: "",
    payment_mode: "",
    amount: "",
    reference_no: "",
    bank_name_id: "",
    bank_name: "",
    branch_name: "",
    cheque_date: "",
    upi_transaction_id: "",
    card_last_4_digits: "",
    remarks: "",
    approver_id: "",
    status: "",
    approval_status: "",
    is_draft: true,
  });

  // Step 2: Invoice allocation
  const [invoiceAllocations, setInvoiceAllocations] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [isOnAccount, setIsOnAccount] = useState(false);
  const [receiptSource, setReceiptSource] = useState("new_payment");
  const [pendingAllocation, setPendingAllocation] = useState(false);
  const [onAccountRemainder, setOnAccountRemainder] = useState(0);

  const isOnAccountDeposit = receiptSource === "on_account_deposit";

  // Data
  const [customers, setCustomers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const canApprove = hasPermission("ar-receipt", "approve");
  // Customer summary data
  const [customerSummary, setCustomerSummary] = useState(null);

  const paymentModes = ["Cash", "Cheque", "Bank Transfer", "UPI", "Credit Card", "Debit Card"];

  const resolveLineItemTypeCode = (item) => {
    const match = deductionTypes.find(
      (type) => String(type.id) === String(item.deduction_type_id)
    );
    return match?.code || item.deduction_type_code || "";
  };

  const getAllocationSummary = (allocation) =>
    calculateAllocationSummary(
      allocation.allocated_amount,
      allocation.deductions,
      resolveLineItemTypeCode
    );

  const getOutstandingAfterReceipt = (allocation) =>
    calculateTotalOutstandingAfterReceipt(
      allocation.outstanding_amount,
      getAllocationSummary(allocation)
    );

  const getOnAccountAvailable = () =>
    roundMoney(customerSummary?.on_account_balance || 0);

  const getAllocationPool = () =>
    roundMoney(parseFloat(formData.amount) || 0);

  const getDepositAllocationPool = () => {
    if (isAllocationPhase && receiptSource === "new_payment") {
      return onAccountRemainder;
    }
    return roundMoney(parseFloat(formData.amount) || 0);
  };

  const getCashPortionFromReceipt = () =>
    roundMoney(
      Math.max(
        0,
        roundMoney(parseFloat(formData.amount) || 0) - onAccountRemainder
      )
    );

  const isAllocationPhase =
    isEditMode &&
    pendingAllocation &&
    formData.approval_status === "Approved";

  const skipAllocationStep =
    isOnAccountDeposit && !isAllocationPhase;

  const getStepConfig = () => {
    if (isAllocationPhase) {
      return [
        { step: 2, label: "Invoice Allocation" },
        { step: 3, label: "Review & Submit" },
      ];
    }
    if (skipAllocationStep) {
      return [
        { step: 1, label: "Receipt Details" },
        { step: 3, label: "Review & Submit" },
      ];
    }
    return [
      { step: 1, label: "Receipt Details" },
      { step: 2, label: "Invoice Allocation" },
      { step: 3, label: "Review & Submit" },
    ];
  };

  const stepConfig = getStepConfig();
  const isLastStep = currentStep === stepConfig[stepConfig.length - 1].step;

  const getTotalAllocated = () =>
    roundMoney(
      invoiceAllocations.reduce(
        (sum, alloc) => sum + parseFloat(alloc.allocated_amount || 0),
        0
      )
    );

  const getOnAccountCreditAmount = () => {
    if (isOnAccountDeposit || isAllocationPhase) {
      return 0;
    }
    const receiptAmount = roundMoney(formData.amount);
    const totalAllocated = getTotalAllocated();
    const cashFromReceipt = roundMoney(Math.min(totalAllocated, receiptAmount));
    return roundMoney(receiptAmount - cashFromReceipt);
  };

  const getOnAccountUsedAmount = () => {
    if (isAllocationPhase) {
      if (receiptSource === "new_payment") {
        const totalAllocated = getTotalAllocated();
        const cashPortion = getCashPortionFromReceipt();
        return roundMoney(Math.max(0, totalAllocated - cashPortion));
      }
      return getTotalAllocated();
    }
    const receiptAmount = roundMoney(formData.amount);
    return roundMoney(Math.max(0, getTotalAllocated() - receiptAmount));
  };

  useEffect(() => {
    fetchCustomers();
    fetchApprovers();
    fetchBanks();
    fetchDeductionTypes();
    if (isEditMode) {
      fetchReceipt();
    }
  }, [rowId]);

  useEffect(() => {
    if (formData.customer_id && (currentStep === 2 || isAllocationPhase)) {
      fetchCustomerSummary(formData.customer_id);
      fetchCustomerInvoices(formData.customer_id);
    } else if (!formData.customer_id) {
      setCustomerSummary(null);
    }
  }, [formData.customer_id, currentStep, isAllocationPhase]);

  const fetchCustomers = async () => {
    try {
      const data = await arService.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await arService.getReceiptApprovers();
      setApprovers(response || []);
    } catch (error) {
      console.error("Error fetching approvers:", error);
      setApprovers([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const data = await arService.getBanks();
      setBanks(data);
    } catch (error) {
      console.error("Error fetching banks:", error);
      setBanks([]);
    }
  };

  const fetchDeductionTypes = async () => {
    try {
      const data = await arService.getDeductionTypes();
      setDeductionTypes(data);
    } catch (error) {
      console.error("Error fetching deduction types:", error);
      setDeductionTypes([]);
    }
  };

  const fetchCustomerInvoices = async (customerId) => {
    try {
      const data = await arService.getCustomerOutstandingInvoices(customerId);
      setAvailableInvoices(data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setAvailableInvoices([]);
    }
  };

  const fetchCustomerSummary = async (customerId) => {
    try {
      const data = await arService.getCustomerBalance(customerId);
      setCustomerSummary(data);
    } catch (error) {
      console.error("Error fetching customer summary:", error);
      setCustomerSummary(null);
    }
  };

  const fetchReceipt = async () => {
    try {
      setIsLoading(true);
      const data = await arService.getReceiptById(rowId);
      
      setFormData({
        receipt_date: data.receipt_date ? data.receipt_date.split("T")[0] : "",
        customer_id: data.customer_id || "",
        payment_mode: data.payment_mode || "",
        amount: data.amount || data.receipt_amount || "",
        reference_no: data.reference_no || "",
        bank_name_id: data.bank_name_id || "",
        bank_name: data.bank_name || "",
        branch_name: data.branch_name || "",
        cheque_date: data.cheque_date ? data.cheque_date.split("T")[0] : "",
        upi_transaction_id: data.upi_transaction_id || "",
        card_last_4_digits: data.card_last_4_digits || "",
        remarks: data.remarks || "",
        approver_id: data.approver_id != null && data.approver_id !== ""
          ? String(data.approver_id)
          : "",
        approver_name: data.approver_name || "",
        approver_email: data.approver_email || "",
        status: data.status || "",
        approval_status: data.approval_status || "",
        is_draft: data.is_draft ?? data.approval_status === "Draft",
      });

      setPendingAllocation(!!data.pending_allocation);
      setOnAccountRemainder(
        roundMoney(
          parseFloat(data.on_account_amount || data.unallocated_amount || 0) -
            parseFloat(data.on_account_used || 0)
        )
      );

      if (data.receipt_type === "on_account_deposit") {
        setReceiptSource("on_account_deposit");
      } else {
        setReceiptSource("new_payment");
      }

      setIsOnAccount(!!data.is_on_account && data.receipt_type !== "on_account_deposit");

      if (data.pending_allocation && data.approval_status === "Approved") {
        setCurrentStep(2);
      }

      // Preserve invoice allocations with deductions (TDS, Round Off, etc.)
      const allocationsData = data.invoice_allocations || data.allocations || [];
      
      if (allocationsData.length > 0) {
        const loadedAllocations = allocationsData.map(alloc => ({
          invoice_id: alloc.invoice_id,
          invoice_no: alloc.invoice_no || alloc.doc_no,
          invoice_amount: alloc.invoice_total || alloc.invoice_amount || alloc.amount || 0,
          outstanding_amount: alloc.outstanding_amount || alloc.outstanding || alloc.balance_due || alloc.invoice_total || 0,
          taxable_amount: alloc.taxable_amount || 0,
          allocated_amount: alloc.allocated_amount || alloc.amount_allocated || 0,
          deductions: Array.isArray(alloc.deductions) ? alloc.deductions.map(ded => ({
            deduction_type_id: ded.deduction_type_id,
            deduction_type_name: ded.deduction_type_name,
            deduction_amount: ded.deduction_amount != null ? String(ded.deduction_amount) : '',
            rate_percentage: ded.rate_percentage != null ? String(ded.rate_percentage) : '',
            remarks: ded.remarks || ''
          })) : []
        }));
        setInvoiceAllocations(loadedAllocations);
        console.log('Loaded allocations:', loadedAllocations);
      }
    } catch (error) {
      console.error("Error fetching receipt:", error);
      showMessageBox("Failed to fetch receipt details.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReceiptSourceChange = (e) => {
    const value = e.target.value;
    setReceiptSource(value);
    if (value === "on_account_deposit") {
      setIsOnAccount(true);
      setInvoiceAllocations([]);
    } else {
      setIsOnAccount(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate cheque date if payment mode is Cheque
    if (name === "cheque_date" && formData.payment_mode === "Cheque" && value) {
      const chequeDate = new Date(value);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      if (chequeDate < sixMonthsAgo) {
        showMessageBox("Cheque date cannot be older than 6 months. Please enter a valid cheque date.", "error");
        return; // Don't update the field
      }
    }
    
    // When bank is selected, also store the bank name
    if (name === "bank_name_id" && value) {
      const selectedBank = banks.find(bank => bank.id === value);
      setFormData((prev) => ({ 
        ...prev, 
        bank_name_id: value,
        bank_name: selectedBank ? selectedBank.value : ""
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateStep1 = () => {
    if (!formData.customer_id) {
      showMessageBox("Please select a customer.", "error");
      return false;
    }
    if (!formData.receipt_date) {
      showMessageBox("Please enter receipt date.", "error");
      return false;
    }
    if (!formData.payment_mode) {
      showMessageBox("Please select payment mode.", "error");
      return false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showMessageBox("Please enter a valid amount.", "error");
      return false;
    }
    return true;
  };

  const shouldValidateStep2 = () =>
    !skipAllocationStep || isAllocationPhase;

  const validateStep2 = () => {
    if (!shouldValidateStep2()) {
      return true;
    }

    const receiptAmount = roundMoney(formData.amount);
    const totalAllocated = getTotalAllocated();
    const allocationPool = isAllocationPhase
      ? getDepositAllocationPool()
      : getAllocationPool();

    if (isAllocationPhase) {
      if (invoiceAllocations.length === 0) {
        showMessageBox("Please allocate to at least one invoice.", "error");
        return false;
      }
      if (receiptSource === "new_payment") {
        const receiptAmount = roundMoney(formData.amount);
        const cashPortion = getCashPortionFromReceipt();
        const additionalAlloc = roundMoney(getTotalAllocated() - cashPortion);
        if (additionalAlloc > onAccountRemainder + 0.001) {
          showMessageBox(
            `Additional allocation (₹${additionalAlloc.toFixed(
              2
            )}) exceeds on-account remainder on this receipt (₹${onAccountRemainder.toFixed(2)}).`
          , "error");
          return false;
        }
        if (getTotalAllocated() > receiptAmount + 0.001) {
          showMessageBox(
            `Total allocation (₹${getTotalAllocated().toFixed(
              2
            )}) exceeds receipt amount (₹${receiptAmount.toFixed(2)}).`
          , "error");
          return false;
        }
      } else if (totalAllocated > allocationPool + 0.001) {
        showMessageBox(
          `Total allocation (₹${totalAllocated.toFixed(
            2
          )}) exceeds on-account receipt amount (₹${allocationPool.toFixed(2)}).`
        , "error");
        return false;
      }
    } else if (invoiceAllocations.length === 0) {
      showMessageBox("Please allocate the receipt to invoices.", "error");
      return false;
    }

    if (!isAllocationPhase && totalAllocated > allocationPool + 0.001) {
      showMessageBox(
        `Total allocation (₹${totalAllocated.toFixed(
          2
        )}) exceeds available amount (₹${allocationPool.toFixed(2)}).`
      , "error");
      return false;
    }

    if (isAllocationPhase) {
      for (const alloc of invoiceAllocations) {
        const summary = getAllocationSummary(alloc);
        const invoicePending = roundMoney(alloc.outstanding_amount || 0);
        const gstHoldDue = roundMoney(alloc.gst_on_hold_outstanding || 0);
        const maxNetApplied = invoicePending > 0.001 ? invoicePending : gstHoldDue;

        if (summary.netApplied < 0) {
          showMessageBox(`Net applied amount for invoice ${alloc.invoice_no} cannot be negative.`, "error");
          return false;
        }

        if (!amountsEqual(summary.netApplied, maxNetApplied) && summary.netApplied - maxNetApplied > 0.001) {
          showMessageBox(
            `Net applied (₹${summary.netApplied.toFixed(2)}) for invoice ${alloc.invoice_no} cannot exceed amount due (₹${maxNetApplied.toFixed(2)}).`
          , "error");
          return false;
        }

        for (const item of alloc.deductions || []) {
          if (item.deduction_type_id && (item.deduction_amount === '' || item.deduction_amount === null || item.deduction_amount === undefined)) {
            showMessageBox(`Please enter an amount (+/-) for each line item on invoice ${alloc.invoice_no}.`, "error");
            return false;
          }
        }
      }
      return true;
    }

    const onAccountCredit = getOnAccountCreditAmount();
    const allowPartialOnAccount = onAccountCredit > 0.001;

    if (!allowPartialOnAccount && totalAllocated + 0.001 < receiptAmount) {
      showMessageBox("Receipt amount must be fully allocated to invoices.", "error");
      return false;
    }

    if (!allowPartialOnAccount) {
      const receiptMatch = matchReceiptAmount(
        receiptAmount,
        invoiceAllocations,
        resolveLineItemTypeCode
      );

      if (!receiptMatch.valid) {
        showMessageBox(
          `Receipt amount (₹${receiptAmount.toFixed(2)}) must equal allocated cash (₹${receiptMatch.cashTotal.toFixed(2)}) or full settlement (₹${receiptMatch.settlementTotal.toFixed(2)} incl. TDS/Round Off). Withheld amounts (TDS, Round Off, etc.) are not part of cash received — only GST on Hold is tracked separately.`
        , "error");
        return false;
      }
    }

    for (const alloc of invoiceAllocations) {
      const summary = getAllocationSummary(alloc);
      const invoicePending = roundMoney(alloc.outstanding_amount || 0);
      const gstHoldDue = roundMoney(alloc.gst_on_hold_outstanding || 0);
      const maxNetApplied = invoicePending > 0.001 ? invoicePending : gstHoldDue;

      if (summary.netApplied < 0) {
        showMessageBox(`Net applied amount for invoice ${alloc.invoice_no} cannot be negative.`, "error");
        return false;
      }

      if (!amountsEqual(summary.netApplied, maxNetApplied) && summary.netApplied - maxNetApplied > 0.001) {
        showMessageBox(
          `Net applied (₹${summary.netApplied.toFixed(2)}) for invoice ${alloc.invoice_no} cannot exceed amount due (₹${maxNetApplied.toFixed(2)}).`
        , "error");
        return false;
      }

      for (const item of alloc.deductions || []) {
        if (item.deduction_type_id && (item.deduction_amount === '' || item.deduction_amount === null || item.deduction_amount === undefined)) {
          showMessageBox(`Please enter an amount (+/-) for each line item on invoice ${alloc.invoice_no}.`, "error");
          return false;
        }
      }
    }

    if (
      !isAllocationPhase &&
      (formData.payment_mode === "Cheque" || formData.payment_mode === "Bank Transfer") &&
      !formData.branch_name
    ) {
      showMessageBox("Branch name is required for bank payments.", "error");
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;

    const currentIndex = stepConfig.findIndex((s) => s.step === currentStep);
    if (currentIndex < stepConfig.length - 1) {
      setCurrentStep(stepConfig[currentIndex + 1].step);
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = stepConfig.findIndex((s) => s.step === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepConfig[currentIndex - 1].step);
    }
  };

  const getMaxAllocatableAmount = (invoice) => {
    const invoicePending = parseFloat(invoice.outstanding_amount || 0);
    const gstHold = parseFloat(invoice.gst_on_hold_outstanding || 0);
    if (invoicePending > 0.001) return invoicePending;
    return gstHold;
  };

  const handleInvoiceSelect = (invoice) => {
    const isSelected = invoiceAllocations.some((alloc) => alloc.invoice_id === invoice.id);
    
    if (isSelected) {
      setInvoiceAllocations(invoiceAllocations.filter((alloc) => alloc.invoice_id !== invoice.id));
    } else {
      const receiptAmount = parseFloat(formData.amount) || 0;
      const allocationPool = isAllocationPhase
        ? roundMoney(parseFloat(formData.amount) || 0)
        : receiptAmount;
      const alreadyAllocated = invoiceAllocations.reduce(
        (sum, alloc) => sum + parseFloat(alloc.allocated_amount || 0),
        0
      );
      const remainingPool = Math.max(allocationPool - alreadyAllocated, 0);
      const amountDue =
        parseFloat(invoice.suggested_allocate_amount) ||
        getMaxAllocatableAmount(invoice);
      const allocatedAmount = Math.min(amountDue, remainingPool || allocationPool);

      setInvoiceAllocations([
        ...invoiceAllocations,
        {
          invoice_id: invoice.id,
          invoice_no: invoice.invoice_no,
          invoice_amount: invoice.total_amount,
          outstanding_amount: invoice.outstanding_amount,
          taxable_amount: invoice.taxable_amount || 0,
          gst_on_hold_outstanding: invoice.gst_on_hold_outstanding || 0,
          paid_amount: invoice.paid_amount || invoice.amount_paid || 0,
          payment_status: invoice.payment_status || "Unpaid",
          allocated_amount: allocatedAmount.toFixed(2),
          deductions: [],
        },
      ]);
    }
  };

  const handleAllocationChange = (invoiceId, newAmount) => {
    const newAmountNum = parseFloat(newAmount) || 0;
    
    const currentInvoice = invoiceAllocations.find(alloc => alloc.invoice_id === invoiceId);
    const availableInvoice = availableInvoices.find((inv) => inv.id === invoiceId);
    const maxAllocatable = currentInvoice
      ? Math.max(
          parseFloat(currentInvoice.outstanding_amount || 0),
          parseFloat(currentInvoice.gst_on_hold_outstanding || 0)
        )
      : availableInvoice
      ? getMaxAllocatableAmount(availableInvoice)
      : 0;

    if (currentInvoice && newAmountNum > maxAllocatable + 0.001) {
      showMessageBox(`Allocation amount (₹${newAmountNum.toFixed(2)}) cannot exceed amount due (₹${maxAllocatable.toFixed(2)}).`, "error");
      return;
    }
    
    // Allow any allocation amount - partial payments are allowed
    // The remaining amount will stay as outstanding on the invoice
    setInvoiceAllocations(
      invoiceAllocations.map((alloc) =>
        alloc.invoice_id === invoiceId
          ? { ...alloc, allocated_amount: newAmountNum.toFixed(2) }
          : alloc
      )
    );
  };

  const getDeductionTypeRate = (deductionTypeId) => {
    const type = deductionTypes.find(
      (t) => String(t.id) === String(deductionTypeId)
    );
    if (!type || type.rate_percentage == null || type.rate_percentage === '') {
      return null;
    }
    const rate = parseFloat(type.rate_percentage);
    return Number.isNaN(rate) ? null : rate;
  };

  const handleDeductionChange = (invoiceId, deductionIndex, field, value) => {
    const updatedAllocations = invoiceAllocations.map((alloc) => {
      if (alloc.invoice_id !== invoiceId) return alloc;

      const deductions = [...(alloc.deductions || [])];
      const line = { ...(deductions[deductionIndex] || {}) };
      const taxable = roundMoney(parseFloat(alloc.taxable_amount) || 0);

      if (field === 'deduction_type_id') {
        line.deduction_type_id = value;
        const rate = getDeductionTypeRate(value);
        if (rate != null) {
          // Prefill the configured rate and auto-calculate the amount on the
          // invoice taxable amount (e.g. TDS 2%). Both stay editable below.
          line.rate_percentage = String(rate);
          if (taxable > 0) {
            line.deduction_amount = roundMoney((taxable * rate) / 100).toFixed(2);
          }
        } else {
          line.rate_percentage = '';
        }
      } else if (field === 'rate_percentage') {
        line.rate_percentage = value;
        const rate = value === '' ? null : parseFloat(value);
        if (rate != null && !Number.isNaN(rate) && taxable > 0) {
          line.deduction_amount = roundMoney((taxable * rate) / 100).toFixed(2);
        }
      } else {
        line[field] = value;
      }

      deductions[deductionIndex] = line;
      return { ...alloc, deductions };
    });
    setInvoiceAllocations(updatedAllocations);
  };

  const addLineItem = (invoiceId) => {
    const updatedAllocations = invoiceAllocations.map((alloc) => {
      if (alloc.invoice_id === invoiceId) {
        const deductions = [...(alloc.deductions || [])];
        deductions.push({
          deduction_type_id: '',
          deduction_amount: '',
          rate_percentage: '',
          remarks: ''
        });
        return { ...alloc, deductions };
      }
      return alloc;
    });
    setInvoiceAllocations(updatedAllocations);
  };

  const removeLineItem = (invoiceId, deductionIndex) => {
    const updatedAllocations = invoiceAllocations.map((alloc) => {
      if (alloc.invoice_id === invoiceId) {
        const deductions = [...(alloc.deductions || [])];
        deductions.splice(deductionIndex, 1);
        return { ...alloc, deductions };
      }
      return alloc;
    });
    setInvoiceAllocations(updatedAllocations);
  };

  const getLineItemTypeName = (deductionTypeId) => {
    const match = deductionTypes.find((type) => String(type.id) === String(deductionTypeId));
    return match?.name || '';
  };

  const handleApproval = async (action) => {
    if (action === "Approved") {
      const remark = window.prompt("Enter approval remark (optional):");
      if (remark === null) return;
      try {
        setIsSaving(true);
        await arService.approveReceipt(rowId, remark || "");
        showMessageBox("Receipt approved successfully!", "success");
        navigate(`/ar/receipts/${rowId}/print`);
      } catch (error) {
        console.error("Error approving receipt:", error);
        showMessageBox(error.message || "Failed to approve receipt.", "error");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const remark = window.prompt("Enter rejection reason (required):");
    if (!remark || !remark.trim()) {
      showMessageBox("Rejection reason is required!", "error");
      return;
    }
    try {
      setIsSaving(true);
      await arService.rejectReceipt(rowId, remark.trim());
      showMessageBox("Receipt rejected successfully!", "success");
      navigate("/ar/receipts");
    } catch (error) {
      console.error("Error rejecting receipt:", error);
      showMessageBox(error.message || "Failed to reject receipt.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isAllocationPhase && !validateStep1()) return;
    if (shouldValidateStep2() && !validateStep2()) return;

    // Validate approver if not draft
    if (!isDraft && !formData.approver_id) {
      showMessageBox(
        isAllocationPhase
          ? "Please select an approver to submit the allocation update for approval."
          : "Please select an approver to submit for approval."
      , "error");
      return;
    }

    try {
      setIsSaving(true);

      const { approval_status, ...formDataWithoutApprovalStatus } = formData;
      
      const payload = {
        ...formDataWithoutApprovalStatus,
        branch_name: formData.branch_name,
        receipt_type: receiptSource,
        is_draft: isDraft,
        is_on_account:
          isOnAccountDeposit ||
          getOnAccountCreditAmount() > 0 ||
          getOnAccountUsedAmount() > 0,
        allocation_phase: isAllocationPhase,
        approver_id: formData.approver_id || null,
        invoice_allocations: (skipAllocationStep && !isAllocationPhase)
          ? []
          : invoiceAllocations.map(alloc => ({
          invoice_id: alloc.invoice_id,
          allocated_amount: alloc.allocated_amount,
          deductions: (alloc.deductions || []).map(ded => ({
            deduction_type_id: ded.deduction_type_id,
            deduction_amount: ded.deduction_amount,
            rate_percentage:
              ded.rate_percentage === undefined || ded.rate_percentage === ''
                ? null
                : ded.rate_percentage,
            remarks: ded.remarks || ''
          }))
        }))
      };

      if (isEditMode) {
        await arService.updateReceipt(rowId, payload);
        showMessageBox(isDraft ? "Receipt saved as draft!" : "Receipt submitted for approval!", "success");
      } else {
        await arService.createReceipt(payload);
        showMessageBox(isDraft ? "Receipt saved as draft!" : "Receipt submitted for approval!", "success");
      }

      navigate("/ar/receipts");
    } catch (error) {
      console.error("Error saving receipt:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save receipt. Please try again.";
      showMessageBox(`Error: ${errorMessage}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? Any unsaved changes will be lost.")) {
      navigate("/ar/receipts");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading receipt...</p>
      </div>
    );
  }

  const isPendingApproval = isPendingApprovalStatus(formData.approval_status);
  const isApproved = formData.approval_status === "Approved";
  const isFormDisabled =
    isPendingApproval || (isApproved && !isAllocationPhase);
  const showApproveActions = isEditMode && isPendingApproval && canApprove;

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {stepConfig.map((step, index) => (
        <div
          key={step.step}
          className={`step ${currentStep === step.step ? "active" : ""} ${stepConfig.findIndex((s) => s.step === currentStep) > index ? "completed" : ""}`}
        >
          <div className="step-number">{index + 1}</div>
          <div className="step-label">{step.label}</div>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step">
      <h3>Step 1: Receipt Details</h3>
      
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="receipt_date">
            Receipt Date <span className="required">*</span>
          </label>
          <input
            type="date"
            id="receipt_date"
            name="receipt_date"
            value={formData.receipt_date}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="customer_id">
            Customer <span className="required">*</span>
          </label>
          <select
            id="customer_id"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Customer</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="receipt_source">
            Receipt Type <span className="required">*</span>
          </label>
          <select
            id="receipt_source"
            name="receipt_source"
            value={receiptSource}
            onChange={handleReceiptSourceChange}
            required
            disabled={isAllocationPhase}
          >
            <option value="new_payment">New Payment (Cash / Bank / UPI)</option>
            <option value="on_account_deposit">On Account (deposit for later allocation)</option>
          </select>
          {isOnAccountDeposit && (
            <p className="on-account-note">
              Payment will be credited to the customer on-account balance after approval. You can allocate invoices later by editing this receipt.
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="payment_mode">
            Payment Mode <span className="required">*</span>
          </label>
          <select
            id="payment_mode"
            name="payment_mode"
            value={formData.payment_mode}
            onChange={handleInputChange}
            required
            disabled={isAllocationPhase}
          >
            <option value="">Select Payment Mode</option>
            {paymentModes.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="amount">
            {isOnAccountDeposit ? "On Account Amount" : "Amount"}{" "}
            <span className="required">*</span>
          </label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleInputChange}
            step="0.01"
            min="0"
            placeholder="0.00"
            required
            disabled={isAllocationPhase}
          />
        </div>

        {!isAllocationPhase && (
        <>
        <div className="form-group">
          <label htmlFor="reference_no">Reference No</label>
          <input
            type="text"
            id="reference_no"
            name="reference_no"
            value={formData.reference_no}
            onChange={handleInputChange}
            placeholder="Transaction/Reference Number"
          />
        </div>

        {(formData.payment_mode === "Cheque" || formData.payment_mode === "Bank Transfer") && (
          <>
            <div className="form-group">
              <label htmlFor="bank_name_id">Bank Name</label>
              <select
                id="bank_name_id"
                name="bank_name_id"
                value={formData.bank_name_id}
                onChange={handleInputChange}
              >
                <option value="">Select Bank</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.value}
                  </option>
                ))}
              </select>
            </div>
            {formData.bank_name_id && (
              <div className="form-group">
                <label htmlFor="branch_name">
                  Branch Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="branch_name"
                  name="branch_name"
                  value={formData.branch_name}
                  onChange={handleInputChange}
                  placeholder="Enter branch name"
                  required
                />
              </div>
            )}
            {formData.payment_mode === "Cheque" && (
              <div className="form-group">
                <label htmlFor="cheque_date">Cheque Date</label>
                <input
                  type="date"
                  id="cheque_date"
                  name="cheque_date"
                  value={formData.cheque_date}
                  onChange={handleInputChange}
                />
              </div>
            )}
          </>
        )}

        {formData.payment_mode === "UPI" && (
          <div className="form-group">
            <label htmlFor="upi_transaction_id">UPI Transaction ID</label>
            <input
              type="text"
              id="upi_transaction_id"
              name="upi_transaction_id"
              value={formData.upi_transaction_id}
              onChange={handleInputChange}
            />
          </div>
        )}

        {(formData.payment_mode === "Credit Card" || formData.payment_mode === "Debit Card") && (
          <div className="form-group">
            <label htmlFor="card_last_4_digits">Card Last 4 Digits</label>
            <input
              type="text"
              id="card_last_4_digits"
              name="card_last_4_digits"
              value={formData.card_last_4_digits}
              onChange={handleInputChange}
              maxLength="4"
              placeholder="1234"
            />
          </div>
        )}
        </>
        )}

        <div className="form-group full-width">
          <label htmlFor="remarks">Remarks</label>
          <textarea
            id="remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleInputChange}
            rows="3"
            placeholder="Additional notes..."
          ></textarea>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const totalAllocated = getTotalAllocated();
    const receiptMatch = matchReceiptAmount(
      formData.amount,
      invoiceAllocations,
      resolveLineItemTypeCode
    );
    const allocationPool = isAllocationPhase
      ? getDepositAllocationPool()
      : getAllocationPool();
    const onAccountUsed = getOnAccountUsedAmount();
    const onAccountCredit = getOnAccountCreditAmount();
    const remainingAmount = onAccountCredit > 0.001
      ? onAccountCredit
      : receiptMatch.valid
      ? 0
      : receiptMatch.remaining;

    return (
      <div className="form-step">
        <h3>{isAllocationPhase ? "Allocate Remaining On-Account Amount to Invoices" : "Step 2: Allocate to Invoices"}</h3>

        {isAllocationPhase && (
          <p className="on-account-note">
            {receiptSource === "new_payment"
              ? `Allocate up to ₹ ${onAccountRemainder.toFixed(2)} from this receipt's on-account remainder to outstanding invoices. Existing invoice allocations are preserved.`
              : `Allocate up to ₹ ${getDepositAllocationPool().toFixed(2)} from this on-account receipt to outstanding invoices.`}
          </p>
        )}
        
        {/* Customer Summary Cards */}
        {customerSummary && (
          <div className="customer-summary-cards">
            <h4>Customer Summary</h4>
            <div className="summary-cards-grid">
              <div className="summary-card highlight">
                <div className="card-label">On Account Balance</div>
                <div className="card-value on-account">
                  ₹ {getOnAccountAvailable().toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <div className="card-label">Total Outstanding</div>
                <div className="card-value outstanding">
                  ₹ {parseFloat(customerSummary.total_outstanding || 0).toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <div className="card-label">Total Overdue</div>
                <div className="card-value overdue">
                  ₹ {parseFloat(customerSummary.total_overdue || 0).toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <div className="card-label">Available Credit</div>
                <div className="card-value credit">
                  ₹ {parseFloat(customerSummary.available_credit || 0).toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <div className="card-label">Total Invoiced</div>
                <div className="card-value invoiced">
                  ₹ {parseFloat(customerSummary.total_invoiced || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="allocation-summary">
          <div className="summary-item">
            <span>
              {isAllocationPhase && receiptSource !== "new_payment"
                ? "On-Account Receipt Amount:"
                : "Receipt Amount:"}
            </span>
            <strong>₹ {parseFloat(formData.amount || 0).toFixed(2)}</strong>
          </div>
          {isAllocationPhase && receiptSource === "new_payment" && (
            <div className="summary-item">
              <span>Available to Allocate Now:</span>
              <strong>₹ {onAccountRemainder.toFixed(2)}</strong>
            </div>
          )}
          {!isAllocationPhase && (
            <div className="summary-item">
              <span>Total Available for Allocation:</span>
              <strong>₹ {allocationPool.toFixed(2)}</strong>
            </div>
          )}
          {isAllocationPhase && receiptSource !== "new_payment" && (
            <div className="summary-item">
              <span>Total Available for Allocation:</span>
              <strong>₹ {allocationPool.toFixed(2)}</strong>
            </div>
          )}
          <div className="summary-item">
            <span>Allocated (cash):</span>
            <strong>₹ {totalAllocated.toFixed(2)}</strong>
          </div>
          {isAllocationPhase && onAccountUsed > 0 && (
            <div className="summary-item">
              <span>Allocating from on-account remainder:</span>
              <strong>₹ {onAccountUsed.toFixed(2)}</strong>
            </div>
          )}
          {!isAllocationPhase && onAccountCredit > 0.001 && (
            <div className="summary-item highlight">
              <span>Remaining → On Account:</span>
              <strong className="text-success">₹ {onAccountCredit.toFixed(2)}</strong>
            </div>
          )}
          {receiptMatch.valid && receiptMatch.mode === "settlement" && receiptMatch.cashTotal !== receiptMatch.settlementTotal && (
            <div className="summary-item">
              <span>Cash received:</span>
              <strong>₹ {receiptMatch.cashTotal.toFixed(2)}</strong>
            </div>
          )}
          {receiptMatch.valid && receiptMatch.mode === "cash" && receiptMatch.cashTotal !== receiptMatch.settlementTotal && (
            <div className="summary-item">
              <span>Full settlement (incl. withheld):</span>
              <strong>₹ {receiptMatch.settlementTotal.toFixed(2)}</strong>
            </div>
          )}
          {!receiptMatch.valid && receiptMatch.settlementTotal !== totalAllocated && (
            <div className="summary-item">
              <span>Full settlement (incl. withheld):</span>
              <strong>₹ {receiptMatch.settlementTotal.toFixed(2)}</strong>
            </div>
          )}
          <div className="summary-item">
            <span>{onAccountCredit > 0.001 ? "Unallocated (On Account):" : "Remaining:"}</span>
            <strong className={remainingAmount < 0 ? "text-danger" : "text-success"}>
              ₹ {remainingAmount.toFixed(2)}
            </strong>
          </div>
        </div>

        <div className="invoice-selection">
          <h4>Available Invoices</h4>
          {availableInvoices.length === 0 ? (
            <p className="no-data">No outstanding invoices found for this customer.</p>
          ) : (
            <div className="invoice-list">
              {availableInvoices.map((invoice) => {
                const isSelected = invoiceAllocations.some((alloc) => alloc.invoice_id === invoice.id);
                const allocation = invoiceAllocations.find((alloc) => alloc.invoice_id === invoice.id);

                return (
                  <div key={invoice.id} className={`invoice-item ${isSelected ? "selected" : ""}`}>
                    <div className="invoice-header">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleInvoiceSelect(invoice)}
                      />
                      <div className="invoice-info">
                        <strong>{invoice.invoice_no}</strong>
                        <span className="invoice-date">
                          {new Date(invoice.invoice_date).toLocaleDateString()}
                        </span>
                        <span className={`payment-status-badge status-${(invoice.payment_status || 'Unpaid').replace(/\s+/g, '-').toLowerCase()}`}>
                          {invoice.payment_status || 'Unpaid'}
                        </span>
                      </div>
                      <div className="invoice-amounts">
                        {(() => {
                          const totalAmt = parseFloat(invoice.total_amount) || 0;
                          const taxableAmt = parseFloat(invoice.taxable_amount) || 0;
                          const taxAmt = Math.max(totalAmt - taxableAmt, 0);
                          return taxableAmt > 0 ? (
                            <>
                              <div>Taxable Amount: ₹{taxableAmt.toFixed(2)}</div>
                              <div>Tax: ₹{taxAmt.toFixed(2)}</div>
                            </>
                          ) : null;
                        })()}
                        <div>Total: ₹{parseFloat(invoice.total_amount).toFixed(2)}</div>
                        <div>Paid: ₹{parseFloat(invoice.paid_amount || invoice.amount_paid || 0).toFixed(2)}</div>
                        <div>Invoice Pending: ₹{parseFloat(invoice.outstanding_amount).toFixed(2)}</div>
                        {parseFloat(invoice.gst_on_hold_outstanding || 0) > 0 && (
                          <div>GST on Hold: ₹{parseFloat(invoice.gst_on_hold_outstanding).toFixed(2)}</div>
                        )}
                        {parseFloat(invoice.total_outstanding_with_gst_hold || 0) >
                          parseFloat(invoice.outstanding_amount || 0) && (
                          <div className="total-outstanding">
                            Total Outstanding: ₹
                            {parseFloat(
                              invoice.total_outstanding_with_gst_hold || invoice.outstanding_amount
                            ).toFixed(2)}
                          </div>
                        )}
                      </div>
                      {invoice.outstanding_breakdown && invoice.outstanding_breakdown.length > 0 && (
                        <div className="outstanding-breakdown">
                          <strong>Outstanding Details:</strong>
                          <ul>
                            {invoice.outstanding_breakdown.map((item, idx) => (
                              <li key={idx}>
                                {item.type}: ₹{Math.abs(parseFloat(item.amount || 0)).toFixed(2)}
                                {item.purpose ? ` — ${item.purpose}` : ''}
                                {item.receipt_no ? ` (${item.receipt_no})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="allocation-details">
                        <div className="allocation-input">
                          <label>Allocate Amount:</label>
                          <input
                            type="number"
                            value={allocation.allocated_amount}
                            onChange={(e) => handleAllocationChange(invoice.id, e.target.value)}
                            step="0.01"
                            min="0"
                            max={getMaxAllocatableAmount(invoice)}
                          />
                        </div>
                        
                        {/* Line Items Section */}
                        <div className="deduction-section">
                          <div className="deduction-header">
                            <h5>Line Items</h5>
                            <button
                              type="button"
                              className="btn-add-deduction"
                              onClick={() => addLineItem(invoice.id)}
                            >
                              + Add Line Item
                            </button>
                          </div>
                          
                          {(allocation.deductions || []).map((deduction, index) => (
                            <div key={index} className="deduction-item">
                              <div className="deduction-row">
                                <select
                                  value={deduction.deduction_type_id || ''}
                                  onChange={(e) => handleDeductionChange(invoice.id, index, 'deduction_type_id', e.target.value)}
                                  className="deduction-type-select"
                                >
                                  <option value="">Select Line Item Type</option>
                                  {deductionTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                      {type.name}
                                    </option>
                                  ))}
                                </select>

                                {getDeductionTypeRate(deduction.deduction_type_id) != null && (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={deduction.rate_percentage ?? ''}
                                    onChange={(e) => handleDeductionChange(invoice.id, index, 'rate_percentage', e.target.value)}
                                    placeholder="%"
                                    title="Rate % (auto-calculates amount on invoice taxable amount)"
                                    className="deduction-rate-input"
                                  />
                                )}
                                
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={deduction.deduction_amount ?? ''}
                                  onChange={(e) => handleDeductionChange(invoice.id, index, 'deduction_amount', e.target.value)}
                                  placeholder="Amount (+/-)"
                                  className="deduction-amount-input"
                                />
                                
                                <input
                                  type="text"
                                  value={deduction.remarks || ''}
                                  onChange={(e) => handleDeductionChange(invoice.id, index, 'remarks', e.target.value)}
                                  placeholder="Remarks (optional)"
                                  className="deduction-remarks-input"
                                />
                                
                                <button
                                  type="button"
                                  className="btn-remove-deduction"
                                  onClick={() => removeLineItem(invoice.id, index)}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                          
                          {(allocation.deductions || []).length === 0 && (
                            <div className="no-deductions">
                              <p>No line items added. Use + Add Line Item for GST on Hold, GST Received, etc.</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Allocation Summary */}
                        {(() => {
                          const summary = getAllocationSummary(allocation);
                          const pending = getOutstandingAfterReceipt(allocation);
                          return (
                            <div className="allocation-summary-details">
                              <div className="net-amount">
                                <label>Balance Allocation:</label>
                                <span className="net-amount-value">
                                  ₹ {summary.netApplied.toFixed(2)}
                                </span>
                                <small className="calculation-info">
                                  (Allocated: ₹{parseFloat(allocation.allocated_amount || 0).toFixed(2)}
                                  {summary.deductionAppliedTotal !== 0 && (
                                    <> + TDS / Deductions: {formatSignedAmount(summary.deductionAppliedTotal)}</>
                                  )}
                                  {summary.gstReceivedTotal !== 0 && (
                                    <> + GST Received: {formatSignedAmount(summary.gstReceivedTotal)}</>
                                  )}
                                  {summary.gstHoldTotal !== 0 && (
                                    <> + GST on Hold: {formatSignedAmount(summary.gstHoldTotal)}</>
                                  )}
                                  )
                                </small>
                              </div>
                              {summary.gstHoldTotal !== 0 && (
                                <div className="gst-hold-amount">
                                  <label>GST on Hold (kept in outstanding):</label>
                                  <span>{formatSignedAmount(summary.gstHoldTotal)}</span>
                                </div>
                              )}
                              {(() => {
                                const suggested = calculateSuggestedAllocateAmount(
                                  allocation.outstanding_amount,
                                  allocation.deductions,
                                  resolveLineItemTypeCode
                                );
                                const allocated = roundMoney(allocation.allocated_amount || 0);
                                if (
                                  (allocation.deductions || []).length > 0 &&
                                  !amountsEqual(allocated, suggested)
                                ) {
                                  return (
                                    <small className="calculation-info suggested-allocate">
                                      Suggested allocate (cash): ₹{suggested.toFixed(2)}
                                      {" "}(Invoice − withheld − GST on Hold)
                                    </small>
                                  );
                                }
                                return null;
                              })()}
                              <div className="pending-after-receipt">
                                <label>Remaining Outstanding After Receipt:</label>
                                <span>₹ {pending.totalPending.toFixed(2)}</span>
                                {pending.gstOnHold > 0 && pending.invoicePending > 0 && (
                                  <small className="calculation-info">
                                    (includes GST on Hold ₹{pending.gstOnHold.toFixed(2)})
                                  </small>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const customerName = customers.find((c) => c.id === parseInt(formData.customer_id))?.name || "N/A";

    return (
      <div className="form-step">
        <h3>Step 3: Review & Submit</h3>
        
        <div className="review-section">
          <h4>Receipt Details</h4>
          <div className="review-grid">
            <div className="review-item">
              <span>Receipt Date:</span>
              <strong>{formData.receipt_date}</strong>
            </div>
            <div className="review-item">
              <span>Customer:</span>
              <strong>{customerName}</strong>
            </div>
            <div className="review-item">
              <span>Payment Mode:</span>
              <strong>{formData.payment_mode}</strong>
            </div>
            <div className="review-item">
              <span>Amount:</span>
              <strong>₹ {parseFloat(formData.amount).toFixed(2)}</strong>
            </div>
            <div className="review-item">
              <span>On Account:</span>
              <strong>
                {getOnAccountCreditAmount() > 0 || getOnAccountUsedAmount() > 0
                  ? "Yes"
                  : "No"}
              </strong>
            </div>
            {getOnAccountCreditAmount() > 0 && (
              <div className="review-item">
                <span>Amount to On Account:</span>
                <strong>₹ {getOnAccountCreditAmount().toFixed(2)}</strong>
              </div>
            )}
            {getOnAccountUsedAmount() > 0 && (
              <div className="review-item">
                <span>Using from On Account:</span>
                <strong>₹ {getOnAccountUsedAmount().toFixed(2)}</strong>
              </div>
            )}
            {formData.reference_no && (
              <div className="review-item">
                <span>Reference No:</span>
                <strong>{formData.reference_no}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="review-section">
          <h4>Invoice Allocations</h4>
          {invoiceAllocations.length === 0 ? (
            <p className="no-data">No invoice allocations — amount will be held On Account.</p>
          ) : (
          <table className="allocation-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Invoice Amount</th>
                <th>Paid</th>
                <th>Pending</th>
                <th>Allocated</th>
                <th>Deductions Credited / GST Received</th>
                <th>GST on Hold</th>
                <th>Balance Allocation</th>
                <th>Total Outstanding After</th>
              </tr>
            </thead>
            <tbody>
              {invoiceAllocations.map((alloc) => {
                const summary = getAllocationSummary(alloc);
                const pending = getOutstandingAfterReceipt(alloc);
                const netLineItems = summary.deductionAppliedTotal + summary.gstReceivedTotal;
                return (
                <tr key={alloc.invoice_id}>
                  <td>{alloc.invoice_no}</td>
                  <td>₹ {parseFloat(alloc.invoice_amount).toFixed(2)}</td>
                  <td>₹ {parseFloat(alloc.paid_amount || 0).toFixed(2)}</td>
                  <td>₹ {parseFloat(alloc.outstanding_amount).toFixed(2)}</td>
                  <td><strong>₹ {parseFloat(alloc.allocated_amount).toFixed(2)}</strong></td>
                  <td>{netLineItems !== 0 ? formatSignedAmount(netLineItems) : "-"}</td>
                  <td>{summary.gstHoldTotal !== 0 ? formatSignedAmount(summary.gstHoldTotal) : "-"}</td>
                  <td><strong>₹ {summary.netApplied.toFixed(2)}</strong></td>
                  <td><strong>₹ {pending.totalPending.toFixed(2)}</strong></td>
                </tr>
                );
              })}
              <tr className="total-row">
                <td colSpan="4"><strong>Totals:</strong></td>
                <td>
                  <strong>
                    ₹ {invoiceAllocations.reduce((sum, alloc) => sum + parseFloat(alloc.allocated_amount || 0), 0).toFixed(2)}
                  </strong>
                </td>
                <td>
                  <strong>
                    {formatSignedAmount(
                      invoiceAllocations.reduce(
                        (sum, alloc) =>
                          sum +
                          getAllocationSummary(alloc).deductionAppliedTotal +
                          getAllocationSummary(alloc).gstReceivedTotal,
                        0
                      )
                    )}
                  </strong>
                </td>
                <td>
                  <strong>
                    ₹ {invoiceAllocations
                      .reduce((sum, alloc) => sum + getAllocationSummary(alloc).gstHoldTotal, 0)
                      .toFixed(2)}
                  </strong>
                </td>
                <td>
                  <strong>
                    ₹ {invoiceAllocations
                      .reduce((sum, alloc) => sum + getAllocationSummary(alloc).netApplied, 0)
                      .toFixed(2)}
                  </strong>
                </td>
                <td>
                  <strong>
                    ₹ {invoiceAllocations
                      .reduce((sum, alloc) => sum + getOutstandingAfterReceipt(alloc).totalPending, 0)
                      .toFixed(2)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
          )}
        </div>

        {invoiceAllocations.some(alloc => alloc.deductions && alloc.deductions.length > 0) && (
          <div className="review-section">
            <h4>Line Items</h4>
            <table className="allocation-table deductions-table">
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Line Item Type</th>
                  <th>Amount (+/-)</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {invoiceAllocations.map((alloc) => 
                  (alloc.deductions || []).map((deduction, idx) => {
                    const signedAmount = parseSignedAmount(deduction.deduction_amount);
                    return (
                      <tr key={`${alloc.invoice_id}-${idx}`}>
                        <td>{alloc.invoice_no}</td>
                        <td>{deduction.deduction_type_name || getLineItemTypeName(deduction.deduction_type_id) || 'N/A'}</td>
                        <td className={signedAmount < 0 ? 'negative-amount' : 'positive-amount'}>
                          <strong>{formatSignedAmount(signedAmount)}</strong>
                        </td>
                        <td>{deduction.remarks || '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Net Amount Summary */}
        <div className="review-section">
          <h4>Summary</h4>
          <div className="summary-grid">
            <div className="summary-item">
              <span>Receipt Amount:</span>
              <strong>₹ {parseFloat(formData.amount).toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Allocated:</span>
              <strong>₹ {invoiceAllocations.reduce((sum, alloc) => sum + parseFloat(alloc.allocated_amount || 0), 0).toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total TDS Credited / GST Received:</span>
              <strong>
                {formatSignedAmount(
                  invoiceAllocations.reduce(
                    (sum, alloc) =>
                      sum +
                      getAllocationSummary(alloc).deductionAppliedTotal +
                      getAllocationSummary(alloc).gstReceivedTotal,
                    0
                  )
                )}
              </strong>
            </div>
            <div className="summary-item">
              <span>Total GST on Hold:</span>
              <strong>
                ₹ {invoiceAllocations
                  .reduce((sum, alloc) => sum + getAllocationSummary(alloc).gstHoldTotal, 0)
                  .toFixed(2)}
              </strong>
            </div>
            <div className="summary-item total-item">
              <span>Total Balance Allocation:</span>
              <strong>
                ₹ {invoiceAllocations
                  .reduce((sum, alloc) => sum + getAllocationSummary(alloc).netApplied, 0)
                  .toFixed(2)}
              </strong>
            </div>
            <div className="summary-item total-item">
              <span>Total Outstanding After Receipt:</span>
              <strong>
                ₹ {invoiceAllocations
                  .reduce((sum, alloc) => sum + getOutstandingAfterReceipt(alloc).totalPending, 0)
                  .toFixed(2)}
              </strong>
            </div>
          </div>
        </div>

        <div className="review-section">
          <h4>Approval</h4>
          <div className="approval-status-row">
            {getStatusBadge(formData.approval_status, formData.is_draft)}
          </div>
          {isPendingApproval && formData.approver_id ? (
            <div className="form-group">
              <label>Assigned Approver:</label>
              <div className="approver-info">
                {approvers.find(a => String(a.id) === String(formData.approver_id))?.full_name
                  || formData.approver_name
                  || 'Unknown'}
                {' '}
                ({approvers.find(a => String(a.id) === String(formData.approver_id))?.email
                  || formData.approver_email
                  || ''})
              </div>
              <small className="help-text">This receipt is locked while pending approval.</small>
            </div>
          ) : !isApproved || isAllocationPhase ? (
            <div className="form-group">
              <label htmlFor="approver_id">
                Select Approver {!formData.is_draft && <span className="required">*</span>}
              </label>
              <select
                id="approver_id"
                name="approver_id"
                value={formData.approver_id}
                onChange={handleInputChange}
                disabled={isPendingApproval}
                className="form-control"
              >
                <option value="">-- Select Approver --</option>
                {approvers.map((approver) => (
                  <option key={approver.id} value={String(approver.id)}>
                    {approver.full_name} ({approver.email})
                  </option>
                ))}
              </select>
              <small className="help-text">
                {isAllocationPhase
                  ? "Required when submitting additional on-account allocation for approval."
                  : "Required when submitting for approval. You can save as draft with an approver selected."}
              </small>
            </div>
          ) : (
            <small className="help-text">This receipt is approved. Open print/PDF to view the final document.</small>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="receipt-form-container">
      <div className="form-header">
        <h2>{isEditMode ? "Edit Receipt" : "Create New Receipt"}</h2>
        {isEditMode && getStatusBadge(formData.approval_status, formData.is_draft)}
      </div>

      {renderStepIndicator()}

      <fieldset disabled={isFormDisabled} className={`receipt-form-fieldset ${isFormDisabled ? "form-readonly" : ""}`}>
      <div className="receipt-form">
        {currentStep === 1 && !isAllocationPhase && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        {!isFormDisabled && (
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          
          {stepConfig.findIndex((s) => s.step === currentStep) > 0 && (
            <button
              type="button"
              className="btn-previous"
              onClick={handlePreviousStep}
              disabled={isSaving}
            >
              ← Previous
            </button>
          )}
          
          {!isLastStep ? (
            <button
              type="button"
              className="btn-next"
              onClick={handleNextStep}
              disabled={isSaving || isFormDisabled}
            >
              Next →
            </button>
          ) : (
            <>
              {showApproveActions ? (
                <div className="approval-actions-inline">
                  <button
                    type="button"
                    className="btn-success"
                    onClick={() => handleApproval("Approved")}
                    disabled={isSaving}
                  >
                    {isSaving ? "Processing..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => handleApproval("Rejected")}
                    disabled={isSaving}
                  >
                    {isSaving ? "Processing..." : "Reject"}
                  </button>
                </div>
              ) : (
                <>
                  {!isAllocationPhase && (
                  <button
                    type="button"
                    className="btn-draft"
                    onClick={() => handleSubmit(true)}
                    disabled={isSaving}
                    title="Save as draft; selected approver will be kept"
                  >
                    {isSaving ? "Saving..." : "Save as Draft"}
                  </button>
                  )}
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={() => handleSubmit(false)}
                    disabled={isSaving}
                    title="Submit for approval"
                  >
                    {isSaving ? "Saving..." : isAllocationPhase ? "Submit Allocation for Approval" : isEditMode ? "Submit for Approval" : "Submit for Approval"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
        )}

        {isFormDisabled && showApproveActions && (
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate("/ar/receipts")}
            >
              Back to List
            </button>
            <button
              type="button"
              className="btn-success"
              onClick={() => handleApproval("Approved")}
              disabled={isSaving}
            >
              {isSaving ? "Processing..." : "Approve"}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleApproval("Rejected")}
              disabled={isSaving}
            >
              {isSaving ? "Processing..." : "Reject"}
            </button>
          </div>
        )}

        {isApproved && (
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate("/ar/receipts")}>
              Back to List
            </button>
            <button
              type="button"
              className="btn-submit"
              onClick={() => navigate(`/ar/receipts/${rowId}/print`)}
            >
              View PDF
            </button>
          </div>
        )}

        {isFormDisabled && !showApproveActions && !isApproved && (
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate("/ar/receipts")}>
              Back to List
            </button>
          </div>
        )}
      </div>
      </fieldset>
    </div>
  );
};

export default ReceiptForm;
